import { Component, OnInit } from 'angular-ts-decorators';

import { SchedulerLike, Subject, never, interval } from 'rxjs';
import { reduce, flow, groupBy, mapValues } from 'lodash';
import { takeUntil, switchMap, map } from 'rxjs/operators';

import { DateSource } from '@shared/app/scripts/services/date-source';
import { Moment } from '@shared/app/scripts/vendor/moment';
import { PeriodType } from '@core/app/scripts/services/period-type';
import { SalesDashboard } from '@core/app/scripts/services/sales-dashboard';
import { ScopeStreams } from '@shared/app/scripts/services/scope-streams';

/**
 * Component that allows the user to see at a glance transaction information with the help of graphs
 */
@Component({
  selector: 'dashboardRetail',
  template: require('./dashboard-retail.html').default,
})
export class DashboardRetailComponent implements OnInit {
  private currentDate: any;
  private newOrdersThisYear: any;
  private salesThisYear: any;
  public zoomValue: any;
  public data: any;
  public series: any;
  public setZoomSubject: Subject<number>;
  public periodSummary: any;

  constructor(
    private $scope: ng.IScope,
    private dateSource: DateSource,
    private moment: Moment,
    private periodType: PeriodType,
    private salesDashboard: SalesDashboard,
    private scopeStreams: ScopeStreams,
    private scheduler: SchedulerLike,
  ) {
    'ngInject';
  }

  ngOnInit() {
    this.currentDate = this.dateSource.now();
    this.salesThisYear = 0;
    this.newOrdersThisYear = 0;

    this.setZoomSubject = new Subject<number>();

    const setZoom$ = this.setZoomSubject
      .asObservable()
      .pipe(takeUntil(this.scopeStreams.isDestroyed(this.$scope)));

    setZoom$.subscribe((zoomValue) => {
      this.zoomValue = zoomValue;

      this.data = undefined;
      this.series = undefined;

      this.getLiveData();
    });

    setZoom$
      .pipe(
        map((zoomValue) => {
          const minute = 60 * 1000;

          switch (zoomValue) {
            case 1:
              return 5 * minute;
            case 6:
              return 15 * minute;
            case 12:
              return 30 * minute;
            case 24:
              return 60 * minute;
            case 48:
              return 60 * minute;
          }

          return undefined;
        }),
        switchMap((intervalTime) => {
          if (angular.isNumber(intervalTime)) {
            return interval(intervalTime, this.scheduler);
          }

          return never();
        }),
        takeUntil(this.scopeStreams.isDestroyed(this.$scope)),
      )
      .subscribe(() => {
        this.getLiveData();
      });

    // set the default zoom
    this.setZoomSubject.next(1);

    this.salesDashboard
      .getPeriodSummary(this.periodType.day, this.currentDate)
      .then((value) => {
        this.periodSummary = value;
        this.newOrdersThisYear = value.transactions;
        this.salesThisYear = value.turnover;
      });
  }

  private normalizeHour(date, granularity) {
    // Meaningful values for granularity:
    // 1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30, 60
    const dateMoment = this.moment(date),
      roundMinutes =
        granularity * Math.ceil(dateMoment.minutes() / granularity);

    return dateMoment
      .clone()
      .startOf('hour')
      .add(roundMinutes, 'minutes')
      .toDate();
  }

  private getLiveData() {
    let granularity;

    switch (this.zoomValue) {
      case 1:
        granularity = 5;
        break;
      case 6:
        granularity = 15;
        break;
      case 12:
        granularity = 30;
        break;
      case 24:
        granularity = 60;
        break;
      case 48:
        granularity = 120;
        break;
    }

    this.currentDate = this.normalizeHour(this.dateSource.now(), granularity);

    const graphStart = this.moment(this.currentDate)
        .subtract(this.zoomValue, 'hours')
        .toDate(),
      dataStart = this.moment(graphStart)
        .subtract(granularity, 'minutes')
        .toDate();

    this.salesDashboard
      .getPeriod(dataStart, this.currentDate, 'minute')
      .then((data) => {
        let periodDate;

        // Quick & dirty solution! The less playing with data on front-end the better.
        const granularData = flow(
          (x) =>
            groupBy(x, (entity) => {
              entity.date.month -= 1; // normalizeHoure's months are indexed from 0
              return this.normalizeHour(entity.date, granularity);
            }),
          (x) =>
            mapValues(x, (entity) =>
              reduce(entity, (total, row) => total + row.turnover, 0),
            ),
        )(data);

        this.data = [[]];
        this.series = [];

        const addMoment = this.moment(graphStart);

        do {
          periodDate = addMoment.clone().toDate();

          this.series.push(addMoment.format('HH:mm'));
          this.data[0].push(
            granularData[periodDate] ? granularData[periodDate] : 0,
          );

          addMoment.add(granularity, 'minutes');
        } while (addMoment.isSameOrBefore(this.currentDate));

        this.$scope.$broadcast('data:loaded');
      });
  }
}
